import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { COMMERCIAL_TAGS, RESIDENTIAL_TAGS, IMAGES } from "../../data/content";
import { Reveal } from "./Reveal";

const TagGrid = ({ tags, cols = 3 }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols === 3 ? "lg:grid-cols-3" : ""} gap-3`}>
    {tags.map((t) => (
      <span key={t} className="bg-white border border-fc-ink/10 px-4 py-3 font-semibold text-sm text-fc-ink shadow-[0_1px_0_rgba(11,33,64,0.03)]">
        {t}
      </span>
    ))}
  </div>
);

const PhotoCard = ({ src, alt }) => (
  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-fc-ink/10 shadow-[0_30px_70px_rgba(11,44,92,0.15)]">
    <img src={src} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover" />
    <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
  </div>
);

const OrangeButton = ({ children, testid }) => {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testid}
      onClick={() => navigate("/contact")}
      className="group mt-9 inline-flex items-center gap-2 bg-fc-orange text-white px-7 py-4 font-bold uppercase tracking-wide text-sm hover:brightness-95 transition-all"
    >
      {children}
      <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
    </button>
  );
};

export const Commercial = () => (
  <section id="commercial" data-testid="commercial-section" className="surface-light py-24 lg:py-32">
    <div className="mx-auto w-[92%] max-w-[1280px] grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal>
          <span className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-accent">Commercial HVAC</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display font-bold tracking-tight leading-[0.98] text-4xl md:text-6xl text-fc-ink">
            Keep Your Business Comfortable &amp; Operating.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-fc-ink2 text-lg">
            Your HVAC system plays an important role in your business. F&amp;C Air Conditioning
            provides commercial HVAC services designed to support reliable temperature control.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8">
            <TagGrid tags={COMMERCIAL_TAGS} cols={2} />
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <OrangeButton testid="commercial-section-cta">Request Commercial Service</OrangeButton>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <PhotoCard src={IMAGES.commercial} alt="Trane rooftop commercial HVAC unit serviced by F&C" />
      </Reveal>
    </div>
  </section>
);

const ResidentialVisual = () => (
  <div className="relative">
    <PhotoCard src={IMAGES.residential} alt="Rheem air handler installed in a home by F&C" />
    {/* logo-motif badge */}
    <div className="absolute -bottom-6 -left-6 bg-white rounded-xl border border-fc-sky/25 shadow-xl p-4 flex items-center gap-3">
      <svg viewBox="0 0 120 90" className="h-12 w-16">
        <circle cx="30" cy="36" r="16" fill="#F5821F" fillOpacity="0.16" />
        <circle cx="30" cy="36" r="11" fill="#F5821F" />
        <path d="M40 80 L40 50 L70 26 L100 50 L100 80" fill="#ffffff" stroke="#0B2C5C" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M92 30 l4 10 10 4 -10 4 -4 10 -4 -10 -10 -4 10 -4 z" fill="#2FA4E7" />
      </svg>
      <div>
        <div className="font-display text-lg font-bold text-fc-navy leading-none">Home Comfort</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-accent mt-1">Every Season</div>
      </div>
    </div>
  </div>
);

export const Residential = () => (
  <section id="residential" data-testid="residential-section" className="surface-light py-24 lg:py-32">
    <div className="mx-auto w-[92%] max-w-[1280px] grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
      <Reveal>
        <ResidentialVisual />
      </Reveal>
      <div>
        <Reveal>
          <span className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-accent">Residential HVAC</span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display font-bold tracking-tight leading-[0.98] text-4xl md:text-6xl text-fc-ink">
            Comfort for Your Home.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-xl text-fc-ink2 text-lg">
            Your home should be comfortable in every season. F&amp;C Air Conditioning provides
            professional residential HVAC services to help homeowners maintain a comfortable
            indoor environment.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8">
            <TagGrid tags={RESIDENTIAL_TAGS} cols={2} />
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <OrangeButton testid="residential-section-cta">Schedule Residential Service</OrangeButton>
        </Reveal>
      </div>
    </div>
  </section>
);
